<script lang="ts" module>
  /**
   * The Lucide icons this app uses, vendored as path data (Phase 10 plan §2
   * row 5). Two dozen shapes do not justify a package, the same argument
   * `lib/chart` made against a charting library. Paths are Lucide's own,
   * fetched 2026-09-27 from github.com/lucide-icons/lucide `icons/*.svg`; the
   * ISC notice (and the MIT one for the Feather-derived shapes) is in
   * `Icon.LICENSE.txt` beside this file. The arrows, chevrons, `check`,
   * `clock`, `file-text` and `info` came with round 2's surfaces (Stage 10.5),
   * as the handoff draws them; `bell-off`, `pencil`, `grip-vertical` and
   * `share` with its Settings screen; `wifi-off`, `cloud-off`, `refresh-cw`
   * and `download` with its small states.
   *
   * Adding one is a line here. Every element is written as Lucide writes it,
   * so a shape can be checked against the upstream SVG by eye.
   */
  export const ICONS = {
    'arrow-down': [
      ['path', { d: 'M12 5v14' }],
      ['path', { d: 'm19 12-7 7-7-7' }],
    ],
    'arrow-right': [
      ['path', { d: 'M5 12h14' }],
      ['path', { d: 'm12 5 7 7-7 7' }],
    ],
    'arrow-up': [
      ['path', { d: 'm5 12 7-7 7 7' }],
      ['path', { d: 'M12 19V5' }],
    ],
    'chart-line': [
      ['path', { d: 'M3 3v16a2 2 0 0 0 2 2h16' }],
      ['path', { d: 'm19 9-5 5-4-4-3 3' }],
    ],
    check: [['path', { d: 'M20 6 9 17l-5-5' }]],
    'chevron-down': [['path', { d: 'm6 9 6 6 6-6' }]],
    'chevron-up': [['path', { d: 'm18 15-6-6-6 6' }]],
    clock: [
      ['circle', { cx: '12', cy: '12', r: '10' }],
      ['path', { d: 'M12 6v6l4 2' }],
    ],
    'cloud-sun': [
      ['path', { d: 'M12 2v2' }],
      ['path', { d: 'm4.93 4.93 1.41 1.41' }],
      ['path', { d: 'M20 12h2' }],
      ['path', { d: 'm19.07 4.93-1.41 1.41' }],
      ['path', { d: 'M15.947 12.65a4 4 0 0 0-5.925-4.128' }],
      ['path', { d: 'M13 22H7a5 5 0 1 1 4.9-6H13a3 3 0 0 1 0 6Z' }],
    ],
    contrast: [
      ['circle', { cx: '12', cy: '12', r: '10' }],
      ['path', { d: 'M12 18a6 6 0 0 0 0-12v12z' }],
    ],
    'cloud-off': [
      ['path', { d: 'm2 2 20 20' }],
      ['path', { d: 'M5.782 5.782A7 7 0 0 0 9 19h8.5a4.5 4.5 0 0 0 1.307-.193' }],
      ['path', { d: 'M21.532 16.5A4.5 4.5 0 0 0 17.5 10h-1.79A7.008 7.008 0 0 0 10 5.07' }],
    ],
    download: [
      ['path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' }],
      ['polyline', { points: '7 10 12 15 17 10' }],
      ['line', { x1: '12', x2: '12', y1: '15', y2: '3' }],
    ],
    'file-text': [
      ['path', { d: 'M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z' }],
      ['path', { d: 'M14 2v4a2 2 0 0 0 2 2h4' }],
      ['path', { d: 'M10 9H8' }],
      ['path', { d: 'M16 13H8' }],
      ['path', { d: 'M16 17H8' }],
    ],
    info: [
      ['circle', { cx: '12', cy: '12', r: '10' }],
      ['path', { d: 'M12 16v-4' }],
      ['path', { d: 'M12 8h.01' }],
    ],
    bell: [
      ['path', { d: 'M10.268 21a2 2 0 0 0 3.464 0' }],
      [
        'path',
        {
          d: 'M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326',
        },
      ],
    ],
    'bell-off': [
      ['path', { d: 'M10.268 21a2 2 0 0 0 3.464 0' }],
      ['path', { d: 'M17 17H4a1 1 0 0 1-.74-1.673C4.59 13.956 6 12.499 6 8a6 6 0 0 1 .258-1.742' }],
      ['path', { d: 'm2 2 20 20' }],
      ['path', { d: 'M8.668 3.01A6 6 0 0 1 18 8c0 2.687.77 4.653 1.707 6.05' }],
    ],
    'chevron-left': [['path', { d: 'm15 18-6-6 6-6' }]],
    'chevron-right': [['path', { d: 'm9 18 6-6-6-6' }]],
    layers: [
      [
        'path',
        {
          d: 'M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z',
        },
      ],
      ['path', { d: 'M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12' }],
      ['path', { d: 'M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17' }],
    ],
    'locate-fixed': [
      ['line', { x1: '2', x2: '5', y1: '12', y2: '12' }],
      ['line', { x1: '19', x2: '22', y1: '12', y2: '12' }],
      ['line', { x1: '12', x2: '12', y1: '2', y2: '5' }],
      ['line', { x1: '12', x2: '12', y1: '19', y2: '22' }],
      ['circle', { cx: '12', cy: '12', r: '7' }],
      ['circle', { cx: '12', cy: '12', r: '3' }],
    ],
    map: [
      [
        'path',
        {
          d: 'M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z',
        },
      ],
      ['path', { d: 'M15 5.764v15' }],
      ['path', { d: 'M9 3.236v15' }],
    ],
    'map-pin': [
      [
        'path',
        {
          d: 'M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0',
        },
      ],
      ['circle', { cx: '12', cy: '10', r: '3' }],
    ],
    minus: [['path', { d: 'M5 12h14' }]],
    navigation: [['polygon', { points: '3 11 22 2 13 21 11 13 3 11' }]],
    pause: [
      ['rect', { x: '14', y: '3', width: '5', height: '18', rx: '1' }],
      ['rect', { x: '5', y: '3', width: '5', height: '18', rx: '1' }],
    ],
    play: [
      [
        'path',
        {
          d: 'M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z',
        },
      ],
    ],
    plus: [
      ['path', { d: 'M5 12h14' }],
      ['path', { d: 'M12 5v14' }],
    ],
    pencil: [
      [
        'path',
        {
          d: 'M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z',
        },
      ],
    ],
    'grip-vertical': [
      ['circle', { cx: '9', cy: '12', r: '1' }],
      ['circle', { cx: '9', cy: '5', r: '1' }],
      ['circle', { cx: '9', cy: '19', r: '1' }],
      ['circle', { cx: '15', cy: '12', r: '1' }],
      ['circle', { cx: '15', cy: '5', r: '1' }],
      ['circle', { cx: '15', cy: '19', r: '1' }],
    ],
    'refresh-cw': [
      ['path', { d: 'M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8' }],
      ['path', { d: 'M21 3v5h-5' }],
      ['path', { d: 'M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16' }],
      ['path', { d: 'M8 16H3v5' }],
    ],
    search: [
      ['path', { d: 'm21 21-4.34-4.34' }],
      ['circle', { cx: '11', cy: '11', r: '8' }],
    ],
    share: [
      ['path', { d: 'M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8' }],
      ['path', { d: 'm16 6-4-4-4 4' }],
      ['path', { d: 'M12 2v13' }],
    ],
    star: [
      [
        'path',
        {
          d: 'M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z',
        },
      ],
    ],
    'settings-2': [
      ['path', { d: 'M14 17H5' }],
      ['path', { d: 'M19 7h-9' }],
      ['circle', { cx: '17', cy: '17', r: '3' }],
      ['circle', { cx: '7', cy: '7', r: '3' }],
    ],
    'triangle-alert': [
      ['path', { d: 'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3' }],
      ['path', { d: 'M12 9v4' }],
      ['path', { d: 'M12 17h.01' }],
    ],
    'wifi-off': [
      ['path', { d: 'M12 20h.01' }],
      ['path', { d: 'M8.5 16.429a5 5 0 0 1 7 0' }],
      ['path', { d: 'M5 12.859a10 10 0 0 1 5.17-2.69' }],
      ['path', { d: 'M19 12.859a10 10 0 0 0-2.007-1.523' }],
      ['path', { d: 'M2 8.82a15 15 0 0 1 4.177-2.643' }],
      ['path', { d: 'M22 8.82a15 15 0 0 0-11.288-3.764' }],
      ['path', { d: 'm2 2 20 20' }],
    ],
    x: [
      ['path', { d: 'M18 6 6 18' }],
      ['path', { d: 'm6 6 12 12' }],
    ],
  } satisfies Record<string, [string, Record<string, string>][]>

  export type IconName = keyof typeof ICONS
</script>

<script lang="ts">
  /**
   * One icon, drawn in `currentColor` so it takes its button's colour in either
   * theme. Decorative by default: the button around it carries the accessible
   * name, which is what the tests find it by. `filled` fills the shape too,
   * which is how a saved place's star and the play triangle are drawn.
   *
   * Square caps and mitred joins rather than Lucide's round ones, because
   * that is how 1B draws them and the Modernist system has no round corner
   * anywhere.
   */
  let {
    name,
    size = 18,
    strokeWidth = 2,
    filled = false,
  }: { name: IconName; size?: number; strokeWidth?: number; filled?: boolean } = $props()
</script>

<svg
  class="icon"
  width={size}
  height={size}
  viewBox="0 0 24 24"
  fill={filled ? 'currentColor' : 'none'}
  stroke="currentColor"
  stroke-width={strokeWidth}
  stroke-linecap="square"
  stroke-linejoin="miter"
  aria-hidden="true"
  focusable="false"
  data-icon={name}
>
  {#each ICONS[name] as [tag, attrs], i (i)}
    <svelte:element this={tag} {...attrs} />
  {/each}
</svg>

<style>
  .icon {
    display: block;
    flex: none;
  }
</style>
